import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertTriangle, RefreshCw, ShieldCheck, Clock, Banknote, Timer } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { fmt } from './constants';

const money = (v) => fmt(v);

export function ClosedPeriodRecovery({ onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { title, description, items }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/approval-recovery/queue');
      setData(r.data);
    } catch {
      setData({ groups: [], total_entries: 0, disbursed_entries: 0 });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const runApprove = async (items) => {
    if (!items || items.length === 0) return;
    setBusy(true);
    try {
      const r = await api.post('/approval-recovery/approve', { items });
      const d = r.data || {};
      const parts = [];
      if (d.approved) parts.push(`${d.approved} approved`);
      if (d.already_approved) parts.push(`${d.already_approved} already approved`);
      if (d.skipped) parts.push(`${d.skipped} skipped`);
      const msg = parts.join(', ') || 'Nothing to approve';
      if (d.owed_after_disbursement > 0) {
        toast.warning(`${msg}. ${d.owed_after_disbursement} belong to an already-paid period — money is now owed.`, { duration: 8000 });
      } else {
        toast.success(msg);
      }
      if ((d.skipped || 0) > 0 && Array.isArray(d.skipped_items)) {
        const reasons = [...new Set(d.skipped_items.map(s => s.reason).filter(Boolean))];
        if (reasons.length) toast.info(`Skipped: ${reasons.join('; ')}`, { duration: 7000 });
      }
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recovery approval failed');
    }
    setBusy(false);
    setConfirm(null);
  };

  const approveOne = (group, type, id) => {
    const items = [{ type, id }];
    if (group.disbursed) {
      setConfirm({
        title: 'Approve entry in an already-paid period?',
        description: `This period (${group.pay_period_start} → ${group.pay_period_end}) was already Paid / Distributed. Approving this entry creates real money owed to the employee that was never included in that payroll run.`,
        items,
      });
    } else {
      runApprove(items);
    }
  };

  const approveGroup = (group) => {
    const items = [
      ...group.timesheets.map(t => ({ type: 'timesheet', id: t.id })),
      ...group.reimbursements.map(r => ({ type: 'reimbursement', id: r.id })),
    ];
    if (group.disbursed) {
      setConfirm({
        title: 'Approve ALL entries in an already-paid period?',
        description: `This period (${group.pay_period_start} → ${group.pay_period_end}) was already Paid / Distributed. Approving these ${items.length} entries creates real money owed that was never paid out.`,
        items,
      });
    } else {
      runApprove(items);
    }
  };

  if (loading) {
    return (
      <Card className="border-border bg-card shadow-none" data-testid="closed-period-recovery-loading">
        <CardContent className="p-4 text-xs text-muted-foreground animate-pulse">Checking for stranded entries…</CardContent>
      </Card>
    );
  }

  if (!data || data.total_entries === 0) return null;

  const disbursed = data.disbursed_entries || 0;

  return (
    <Card className="border-rose-500/40 bg-rose-500/[0.03] shadow-none" data-testid="closed-period-recovery">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-rose-400 shrink-0 mt-0.5" />
            <div>
              <CardTitle className="text-sm font-heading text-rose-300" data-testid="recovery-title">
                Closed-Period Recovery — {data.total_entries} stranded {data.total_entries === 1 ? 'entry' : 'entries'}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                Draft or Submitted timesheets and reimbursements in periods that already closed for approval.
                They can't be approved from the normal queue. Approve them here.
                {disbursed > 0 && (
                  <span className="text-rose-300 font-medium"> {disbursed} sit in an already-paid period — approving them means money owed.</span>
                )}
              </p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="recovery-refresh-btn">
            <RefreshCw className={`h-3 w-3 mr-1 ${busy ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {data.groups.map(group => {
          const rose = group.disbursed;
          return (
            <div
              key={group.pay_period_start}
              className={`rounded-lg border p-3 ${rose ? 'border-rose-500/40 bg-rose-500/[0.05]' : 'border-amber-500/40 bg-amber-500/[0.04]'}`}
              data-testid={`recovery-group-${group.pay_period_start}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-foreground">
                    {group.pay_period_start} → {group.pay_period_end}
                  </span>
                  {rose ? (
                    <Badge className="text-xs border border-rose-500/50 bg-rose-500/15 text-rose-300 gap-1" data-testid={`recovery-disbursed-badge-${group.pay_period_start}`}>
                      <Banknote className="h-3 w-3" /> Paid / Distributed · money owed
                    </Badge>
                  ) : (
                    <Badge className="text-xs border border-amber-500/50 bg-amber-500/15 text-amber-300 gap-1" data-testid={`recovery-approved-badge-${group.pay_period_start}`}>
                      <Timer className="h-3 w-3" /> Approved · not yet paid
                    </Badge>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {group.timesheet_count} timesheet{group.timesheet_count === 1 ? '' : 's'} · {group.reimbursement_count} reimbursement{group.reimbursement_count === 1 ? '' : 's'}
                  </span>
                </div>
                <Button
                  size="sm"
                  className={rose ? 'bg-rose-500 hover:bg-rose-600 text-white' : ''}
                  variant={rose ? 'default' : 'outline'}
                  onClick={() => approveGroup(group)}
                  disabled={busy}
                  data-testid={`recovery-approve-group-${group.pay_period_start}`}
                >
                  <ShieldCheck className="h-3 w-3 mr-1" /> Approve all in period
                </Button>
              </div>

              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider">Type</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Employee</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Date</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Hours / Amount</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-center">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.timesheets.map(t => (
                    <TableRow key={t.id} className="border-border" data-testid={`recovery-ts-${t.id}`}>
                      <TableCell className="text-xs"><span className="inline-flex items-center gap-1 text-cyan-300"><Clock className="h-3 w-3" /> Timesheet</span></TableCell>
                      <TableCell className="text-xs font-medium">{t.user_name || '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{t.date}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{t.hours || 0}h</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={`text-xs ${t.status === 'Draft' ? 'text-zinc-400 border-zinc-500/30' : 'text-blue-400 border-blue-500/30'}`}>
                          {t.status}{t.rejection_note ? ' (Rejected)' : ''}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => approveOne(group, 'timesheet', t.id)} disabled={busy} data-testid={`recovery-approve-ts-${t.id}`}>
                          Approve
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {group.reimbursements.map(r => (
                    <TableRow key={r.id} className="border-border" data-testid={`recovery-re-${r.id}`}>
                      <TableCell className="text-xs"><span className="inline-flex items-center gap-1 text-orange-300"><Banknote className="h-3 w-3" /> Reimb.{r.payment_source === 'company_card' ? ' (CC)' : ''}</span></TableCell>
                      <TableCell className="text-xs font-medium">{r.user_name || '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{r.date}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{money(r.amount)}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={`text-xs ${r.status === 'Draft' ? 'text-zinc-400 border-zinc-500/30' : 'text-orange-400 border-orange-500/30'}`}>
                          {r.status}{r.rejection_note ? ' (Rejected)' : ''}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => approveOne(group, 'reimbursement', r.id)} disabled={busy} data-testid={`recovery-approve-re-${r.id}`}>
                          Approve
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          );
        })}
      </CardContent>

      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent className="bg-card border-rose-500/40" data-testid="recovery-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-rose-300 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> {confirm?.title}
            </DialogTitle>
            <DialogDescription>{confirm?.description}</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Each approved entry is stamped <span className="font-mono text-rose-300">owed_after_disbursement</span> for audit. This does not automatically pay anyone — arrange repayment or reopen the period as needed.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button className="bg-rose-500 hover:bg-rose-600 text-white" onClick={() => runApprove(confirm.items)} disabled={busy} data-testid="recovery-confirm-approve-btn">
              <ShieldCheck className="h-3 w-3 mr-1" /> Approve anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
